import { addResourceI18n, getI18nHasInstallerResource, getPathToMessages } from '../internal-use';
import { APP_CSS_FILE, APP_STATIC_BUNDLE_FILE, getFailedScriptError, INSTALLER_TOPOLOGY_API_URL, LOAD_SCRIPT_TRANSACTION } from '../utils';
import { createPanoramaClient } from '../panorama';
import { HttpClient } from '@wix/http-client';
const httpClient = new HttpClient();
export let installApp;
let installAppApi;
let panoramaClientPromise;
let panoramaClient;
let scriptLoaded = false;
let cssLoaded = false;
let injectScriptPromise;
let promiseStatus;
let scriptLoadEventCallback;
let cssLinkLoadEventCallback;
let installerTopology;
let rejectScript;
let scriptElement = null;
let cssLinkElement = null;
let isInstallerExecutionRunning = false;
const resetVars = () => {
  scriptLoaded = false;
  cssLoaded = false;
  scriptLoadEventCallback = undefined;
  cssLinkLoadEventCallback = undefined;
  scriptElement = null;
  cssLinkElement = null;
  promiseStatus = undefined;
  installAppApi = undefined;
  rejectScript = undefined;
  installerTopology = undefined;
  isInstallerExecutionRunning = false;
};
export const unmountCleanup = () => {
  // unmount cleanup only if a promise was defined - otherwise promise might still not fulfilled
  if (promiseStatus) {
    if (scriptElement) {
      scriptLoadEventCallback && scriptElement.removeEventListener('load', scriptLoadEventCallback);
      rejectScript && scriptElement.removeEventListener('error', rejectScript);
      document.head.removeChild(scriptElement);
      scriptElement = null;
    }
    if (cssLinkElement) {
      cssLinkLoadEventCallback && cssLinkElement.removeEventListener('load', cssLinkLoadEventCallback);
      rejectScript && cssLinkElement.removeEventListener('error', rejectScript);
      // do not remove cssLink from document.head, because on navigation the stylesheet will be lost
      cssLinkElement = null;
    }
  }

  // should reset vars only if the promise was rejected, so it will fetch api again cleanly
  // otherwise, keep the global vars for unmount-remount situation
  promiseStatus === 'rejected' && resetVars();
};
const scriptLoadEvent = resolveScript => {
  // loading script tags in SSR projects is done only on client-side, so window should be well-defined here
  if (window.__INSTALL_APP__) {
    installAppApi = window.__INSTALL_APP__;
    delete window.__INSTALL_APP__;
    scriptLoaded = true;
    if (cssLoaded) {
      resolveScript();
    }
  } else {
    rejectScript('no window.__INSTALL_APP__ found when script loaded');
  }
};
const cssLinkLoadEvent = resolveScript => {
  cssLoaded = true;
  if (scriptLoaded) {
    resolveScript();
  }
};
const injectScript = async (i18n, _resolve, _reject) => {
  // a global var for the unmount cleanup
  rejectScript = e => {
    promiseStatus = 'rejected';
    const scriptError = getFailedScriptError(panoramaClient, e);
    _reject(scriptError);
  };
  panoramaClient = await panoramaClientPromise; // await for the panorama client to be ready (it's probably resolved by now - initiated at the beginning of the hook)
  panoramaClient.transaction(LOAD_SCRIPT_TRANSACTION).start();
  httpClient.get(INSTALLER_TOPOLOGY_API_URL).then(_ref => {
    let {
      data: {
        topology
      }
    } = _ref;
    if (!topology.appInstallerClientApi) {
      rejectScript('appInstallerClientApi topology is missing');
    }
    const resolveScript = () => {
      promiseStatus = 'resolved';
      _resolve();
    };
    installerTopology = topology;
    const cssHref = topology.appInstallerClientApi + APP_CSS_FILE;
    const scriptSrc = topology.appInstallerClientApi + APP_STATIC_BUNDLE_FILE;
    const isCssLinkPreloaded = Boolean(document.head.querySelector(`link[href="${cssHref}"]`));
    const preloadedScriptElement = document.head.querySelector(`script[src="${scriptSrc}"]`);
    if (isCssLinkPreloaded) {
      // preloaded css can be reused
      cssLoaded = true;
    } else {
      cssLinkElement = document.createElement('link');
      cssLinkElement.rel = 'stylesheet';
      cssLinkElement.href = cssHref;
      cssLinkLoadEventCallback = () => cssLinkLoadEvent(resolveScript);
      cssLinkElement.addEventListener('load', cssLinkLoadEventCallback);
      cssLinkElement.addEventListener('error', rejectScript);
      document.head.appendChild(cssLinkElement);
    }
    scriptElement = document.createElement('script');
    scriptElement.src = scriptSrc;
    scriptLoadEventCallback = () => scriptLoadEvent(resolveScript);
    scriptElement.addEventListener('load', scriptLoadEventCallback);
    scriptElement.addEventListener('error', rejectScript);
    if (preloadedScriptElement) {
      // cannot be reused, so cleanup
      document.head.removeChild(preloadedScriptElement);
    }
    document.head.appendChild(scriptElement);
    const i18nHasInstallerResource = getI18nHasInstallerResource(i18n);
    if (!i18nHasInstallerResource) {
      httpClient // no await because if it will not load resources to i18n before the installer loads, the installer will load it itself - no need to wait for it
      .get(getPathToMessages(topology.translations, i18n.language)).then(_ref2 => {
        let {
          data: messages
        } = _ref2;
        return addResourceI18n(i18n, messages);
      }).catch(() => {
        getFailedScriptError(panoramaClient, `Failed to fetch translations messages for i18n load resource, topology.translations: ${topology.translations}`);
      });
    }
  }).catch(rejectScript);
};
export const implementInstallerInHost = _ref3 => {
  let {
    i18n,
    platform,
    overrideNavigate
  } = _ref3;
  if (installApp) {
    return installApp;
  }
  if (!panoramaClient) {
    panoramaClientPromise = createPanoramaClient(platform);
  }

  // it's a function in order to get the runtime snapshot of promiseStatus inside installApp function
  const wasPromiseRejected = () => promiseStatus === 'rejected';

  // if there is a promise, and it was not rejected, return the installApp that was created already
  // otherwise, let's run it (could be repeated)
  if (!injectScriptPromise || wasPromiseRejected()) {
    const runScriptPromise = () => {
      wasPromiseRejected() && unmountCleanup();
      injectScriptPromise = new Promise((_resolve, _reject) => injectScript(i18n, _resolve, _reject)).finally(() => {
        var _panoramaClient;
        (_panoramaClient = panoramaClient) == null || _panoramaClient.transaction(LOAD_SCRIPT_TRANSACTION).finish();
      });
    };

    // initial run
    runScriptPromise();
    installApp = async installAppProps => {
      if (typeof window === 'undefined') {
        throw new Error('App Installer should not be invoked on SSR');
      }
      if (isInstallerExecutionRunning) {
        throw new Error('Installer is already running');
      }
      const installAppExecution = _installAppApi => {
        isInstallerExecutionRunning = true;
        panoramaClient.reportLoadStart({
          appId: installAppProps.appId,
          tenantId: installAppProps.tenantId,
          url: window.location.href
        });
        return _installAppApi({
          ...installAppProps,
          essentials: {
            httpClient,
            i18n,
            platform
          },
          panoramaClient: panoramaClient,
          topology: installerTopology,
          ...(overrideNavigate ? {
            overrideNavigate
          } : {})
        }).finally(() => {
          isInstallerExecutionRunning = false;
        });
      };
      if (installAppApi && promiseStatus === 'resolved') {
        return installAppExecution(installAppApi);
      } else {
        wasPromiseRejected() && runScriptPromise(); // in case initial run failed, on click will trigger it again
        await injectScriptPromise; // if promise was not resolved yet, wait for it

        // installAppApi could be undefined by type definition
        if (installAppApi) {
          return installAppExecution(installAppApi);
        } else {
          const error = new Error('Failed to return installAppApi after a successful script load');
          throw getFailedScriptError(panoramaClient, error);
        }
      }
    };
  }
  return installApp;
};
//# sourceMappingURL=helperFunctions.js.map